import React, { useMemo } from 'react';

// Code128 patterns (symbols 0 to 106)
const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

export function encodeCode128(text) {
  const clean = String(text || '').trim();
  if (!clean) return null;

  // Code 128 Mode B (ASCII 32..126)
  const codes = [104]; // Start B
  for (let i = 0; i < clean.length; i += 1) {
    const charCode = clean.charCodeAt(i);
    if (charCode >= 32 && charCode <= 126) {
      codes.push(charCode - 32);
    } else {
      codes.push(0); // space fallback
    }
  }

  let checksum = codes[0];
  for (let i = 1; i < codes.length; i += 1) {
    checksum += codes[i] * i;
  }
  codes.push(checksum % 103);
  codes.push(106); // Stop Code

  let totalWidth = 0;
  const rects = [];
  let currentX = 0;

  codes.forEach((code) => {
    const pattern = CODE128_PATTERNS[code];
    if (!pattern) return;
    let isBar = true;
    for (let i = 0; i < pattern.length; i += 1) {
      const w = parseInt(pattern[i], 10);
      if (isBar) {
        rects.push({ x: currentX, width: w });
      }
      currentX += w;
      isBar = !isBar;
    }
  });

  totalWidth = currentX;
  return { rects, totalWidth };
}

/**
 * Generate a single SVG path data string for all black bars in the barcode
 * Each bar is represented as: M x 0 h width v barHeight h -width Z
 */
export function generateBarcodePath(encoded, quietZone = 8, barHeight = 32) {
  if (!encoded || !encoded.rects || encoded.rects.length === 0) return '';
  return encoded.rects
    .map(
      (r) =>
        `M${r.x + quietZone} 0h${r.width}v${barHeight}h-${r.width}Z`,
    )
    .join('');
}

/**
 * Format barcode string with spaces for human readability
 */
export function formatBarcodeText(code) {
  if (!code) return '';
  const str = String(code).trim();
  if (str.length === 13) {
    return `${str.slice(0, 1)} ${str.slice(1, 7)} ${str.slice(7)}`;
  }
  if (str.length > 14) {
    return str.match(/.{1,4}/g)?.join(' ') || str;
  }
  if (str.length >= 8) {
    return `${str.slice(0, Math.ceil(str.length / 2))} ${str.slice(Math.ceil(str.length / 2))}`;
  }
  return str;
}

/**
 * Checks if a code can be reliably printed and scanned as a 1D linear barcode.
 * - Legacy MD5 / SHA hashes (32-char hex) and codes longer than 20 chars are NOT supported for 1D.
 * - Standard numeric codes (EAN-13, EAN-8, UPC) and short alphanumeric SKUs (<= 20 chars) ARE supported.
 */
export function isBarcodeSupported(code) {
  if (!code) return false;
  const str = String(code).trim();
  if (!str) return false;

  // Legacy MD5 / hex hashes (32 chars) are strictly unsupported for 1D barcodes
  if (str.length >= 32 || /^[a-fA-F0-9]{32}$/.test(str)) {
    return false;
  }

  // Codes longer than 20 alphanumeric characters exceed thermal 1D optical readability density
  if (str.length > 20) {
    return false;
  }

  // Must only contain printable ASCII characters (32 to 126) for Code 128
  for (let i = 0; i < str.length; i += 1) {
    const charCode = str.charCodeAt(i);
    if (charCode < 32 || charCode > 126) return false;
  }

  return true;
}

const BarcodeSVG = ({
  value,
  height = 34,
  showText = true,
  className = '',
  style = {},
}) => {
  const safeVal = String(value || '').trim() || '000000';
  const encoded = useMemo(() => encodeCode128(safeVal), [safeVal]);
  const formattedText = useMemo(() => formatBarcodeText(safeVal), [safeVal]);

  const quietZone = 8;
  const svgWidth = (encoded?.totalWidth || 0) + quietZone * 2;
  const barHeight = height;

  const pathData = useMemo(() => {
    if (!encoded) return '';
    return generateBarcodePath(encoded, quietZone, barHeight);
  }, [encoded, quietZone, barHeight]);

  if (!encoded) return null;

  return (
    <div
      className={`barcode-svg-container ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        ...style,
      }}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${barHeight}`}
        preserveAspectRatio="none"
        style={{
          width: '94%',
          height: `${barHeight}px`,
          shapeRendering: 'crispEdges',
          display: 'block',
        }}
      >
        <path d={pathData} fill="#000000" />
      </svg>
      {showText && (
        <span
          style={{
            fontFamily: "'Courier New', Courier, monospace",
            fontSize: '9.5px',
            letterSpacing: '1.6px',
            fontWeight: 700,
            color: '#111',
            marginTop: '2px',
            textAlign: 'center',
            lineHeight: 1,
            wordBreak: 'break-all',
          }}
        >
          {formattedText}
        </span>
      )}
    </div>
  );
};

export default BarcodeSVG;
